/**
 * WebSocketClient
 *
 * A WebSocket client with built-in reconnection logic, state management,
 * and subscriber pattern for messages, state changes, and errors.
 */

import {
    type ConnectionState,
    type WebSocketClientConfig,
    type MessageCallback,
    type StateCallback,
    type ErrorCallback,
    type CloseCallback,
    DEFAULT_WS_CONFIG,
} from './types';

/**
 * How long a socket must stay open before its connection counts as "good" and the reconnect
 * backoff resets. Short enough that an ordinary blip re-retries quickly; long enough that a
 * server flapping open/closed keeps backing off instead of being retried at the base interval.
 */
const STABLE_CONNECTION_MS = 10_000;

/**
 * WebSocket client with automatic reconnection and subscriber pattern.
 *
 * @example
 * ```ts
 * const client = new WebSocketClient({ url: 'wss://example.com/ws' });
 *
 * // Subscribe to messages
 * const unsubscribe = client.onMessage((event) => {
 *   console.log('Received:', event.data);
 * });
 *
 * // Subscribe to state changes
 * client.onStateChange((state) => {
 *   console.log('State:', state);
 * });
 *
 * // Connect
 * client.connect();
 *
 * // Send data
 * client.send('Hello, server!');
 *
 * // Cleanup
 * unsubscribe();
 * client.disconnect();
 * ```
 */
export class WebSocketClient {
    private ws: WebSocket | null = null;
    private config: Required<WebSocketClientConfig>;
    private state: ConnectionState = 'disconnected';

    // Reconnection state
    private reconnectAttempt = 0;
    private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private shouldReconnect = true;
    /** When the current socket opened, for judging whether it was ever stable. */
    private openedAtMs: number | null = null;

    // Subscriber lists
    private messageCallbacks: Set<MessageCallback> = new Set();
    private stateCallbacks: Set<StateCallback> = new Set();
    private errorCallbacks: Set<ErrorCallback> = new Set();
    private closeCallbacks: Set<CloseCallback> = new Set();

    constructor(config: WebSocketClientConfig) {
        this.config = {
            ...DEFAULT_WS_CONFIG,
            ...config,
        };
    }

    /**
     * Connect to the WebSocket server.
     * If already connected or connecting, this is a no-op.
     */
    connect(): void {
        // Guard on the SOCKET, not on the state string. `this.ws` outlives a close (only an
        // explicit disconnect() cleared it), so testing `state !== 'disconnected'` made every
        // scheduled reconnect a silent no-op while the client sat in `reconnecting` forever —
        // the whole reason the chart hung on "Reconnecting…" against a healthy server.
        if (
            this.ws &&
            (this.ws.readyState === WebSocket.OPEN ||
                this.ws.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        this.shouldReconnect = this.config.reconnect;
        // Stay in `reconnecting` across the retry rather than flipping to `connecting`.
        // Consumers detect a recovered stream by the reconnecting -> connected transition
        // (useTradeNotifications fires its REST reconciliation on it); an intermediate
        // `connecting` would make that transition never fire.
        if (this.state !== 'reconnecting') {
            this.setState('connecting');
        }

        try {
            this.ws = new WebSocket(this.config.url, this.config.protocols);
            this.setupEventHandlers();
        } catch (error) {
            this.setState('disconnected');
            // Emit as error event
            this.errorCallbacks.forEach((cb) =>
                cb(new ErrorEvent('error', { error })),
            );
        }
    }

    /**
     * Disconnect from the WebSocket server.
     * Stops any pending reconnection attempts.
     */
    disconnect(): void {
        this.shouldReconnect = false;
        this.clearReconnectTimeout();
        this.reconnectAttempt = 0;
        this.openedAtMs = null;

        if (this.ws) {
            // Remove handlers before closing to prevent reconnect
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            this.ws.onopen = null;

            if (
                this.ws.readyState === WebSocket.OPEN ||
                this.ws.readyState === WebSocket.CONNECTING
            ) {
                this.ws.close(1000, 'Client disconnect');
            }
            this.ws = null;
        }

        this.setState('disconnected');
    }

    /**
     * Send data to the server.
     * @param data - String or binary data to send
     * @throws Error if not connected
     */
    send(data: string | ArrayBuffer): void {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            throw new Error(
                'WebSocket is not connected. Call connect() first.',
            );
        }
        this.ws.send(data);
    }

    /**
     * Force a reconnection attempt.
     * Resets the backoff counter and initiates a new connection.
     */
    reconnect(): void {
        this.disconnect();
        this.reconnectAttempt = 0;
        this.shouldReconnect = this.config.reconnect;
        this.connect();
    }

    /**
     * Subscribe to message events.
     * @returns Unsubscribe function
     */
    onMessage(callback: MessageCallback): () => void {
        this.messageCallbacks.add(callback);
        return () => this.messageCallbacks.delete(callback);
    }

    /**
     * Subscribe to state change events.
     * @returns Unsubscribe function
     */
    onStateChange(callback: StateCallback): () => void {
        this.stateCallbacks.add(callback);
        // Immediately notify of current state
        callback(this.state);
        return () => this.stateCallbacks.delete(callback);
    }

    /**
     * Subscribe to error events.
     * @returns Unsubscribe function
     */
    onError(callback: ErrorCallback): () => void {
        this.errorCallbacks.add(callback);
        return () => this.errorCallbacks.delete(callback);
    }

    /**
     * Subscribe to close events.
     * @returns Unsubscribe function
     */
    onClose(callback: CloseCallback): () => void {
        this.closeCallbacks.add(callback);
        return () => this.closeCallbacks.delete(callback);
    }

    /**
     * Get the current connection state.
     */
    getState(): ConnectionState {
        return this.state;
    }

    /**
     * Get the underlying WebSocket instance.
     * Use with caution - prefer using the client's methods.
     */
    getSocket(): WebSocket | null {
        return this.ws;
    }

    /**
     * Update the URL and reconnect.
     * Useful for switching endpoints without creating a new client.
     */
    setUrl(url: string): void {
        const wasConnected =
            this.state === 'connected' || this.state === 'connecting';
        this.config.url = url;
        if (wasConnected) {
            this.reconnect();
        }
    }

    // --- Private methods ---

    private setState(newState: ConnectionState): void {
        if (this.state !== newState) {
            this.state = newState;
            this.stateCallbacks.forEach((cb) => cb(newState));
        }
    }

    private setupEventHandlers(): void {
        if (!this.ws) return;

        this.ws.onopen = () => {
            // NOT the place to reset the backoff. A server that accepts the handshake and then
            // hangs up (starting, overloaded, auth-rejecting) would reset the counter on every
            // attempt and get hammered at the base interval indefinitely. The reset is earned by
            // STAYING connected — see the stability check in onclose.
            this.openedAtMs = Date.now();
            this.setState('connected');
        };

        this.ws.onmessage = (event: MessageEvent) => {
            this.messageCallbacks.forEach((cb) => cb(event));
        };

        this.ws.onerror = (event: Event) => {
            this.errorCallbacks.forEach((cb) => cb(event));
        };

        this.ws.onclose = (event: CloseEvent) => {
            // drop the dead socket so connect() can tell "no live socket" from "already live"
            this.ws = null;

            // A connection that lasted proves the endpoint is healthy, so the next blip starts
            // from a fast retry instead of inheriting an escalated delay. A connection that died
            // immediately proves nothing and must keep backing off.
            const wasStable =
                this.openedAtMs !== null &&
                Date.now() - this.openedAtMs >= STABLE_CONNECTION_MS;
            this.openedAtMs = null;
            if (wasStable) {
                this.reconnectAttempt = 0;
            }

            this.closeCallbacks.forEach((cb) => cb(event));

            // Reconnect on CLEAN closes too: a server restart or a deploy sends one, and treating
            // it as intentional left the client dead until a manual refresh. An intentional
            // disconnect() is already excluded — it detaches these handlers before closing.
            if (this.shouldReconnect) {
                this.scheduleReconnect();
            } else {
                this.setState('disconnected');
            }
        };
    }

    private scheduleReconnect(): void {
        // Check max attempts
        if (
            this.config.maxReconnectAttempts > 0 &&
            this.reconnectAttempt >= this.config.maxReconnectAttempts
        ) {
            this.setState('disconnected');
            return;
        }

        this.setState('reconnecting');

        const backoff = Math.min(
            this.config.reconnectInterval * Math.pow(2, this.reconnectAttempt),
            this.config.maxReconnectInterval,
        );
        // Full jitter over the lower half of the window. Every client that was connected to a
        // restarting server would otherwise wake at the same instant and retry in lockstep,
        // which is the shape that turns a restart into a thundering herd.
        const delay = backoff * (0.5 + Math.random() * 0.5);

        this.reconnectAttempt++;

        this.reconnectTimeoutId = setTimeout(() => {
            this.reconnectTimeoutId = null;
            this.connect();
        }, delay);
    }

    private clearReconnectTimeout(): void {
        if (this.reconnectTimeoutId !== null) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }
    }
}

/**
 * Factory function to create a WebSocketClient instance.
 */
export function createWebSocketClient(
    config: WebSocketClientConfig,
): WebSocketClient {
    return new WebSocketClient(config);
}
